import { defineStore } from "pinia";
import { ref } from "vue";
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any,
  ) {
    super(message);
  }
}
export const useSession = defineStore("session", () => {
  const user = ref<any>(null),
    organizations = ref<any[]>([]),
    organization = ref(""),
    csrf = ref(""),
    ready = ref(false),
    capabilities = ref<any>({ ai: false, email: false });
  async function refresh() {
    try {
      const r = await fetch("/api/me");
      if (!r.ok) {
        user.value = null;
        return;
      }
      const v = await r.json();
      user.value = v.user;
      organizations.value = v.organizations;
      organization.value = v.organizations.some(
        (o: any) => o.id === organization.value,
      )
        ? organization.value
        : (v.organizations[0]?.id ?? "");
      csrf.value = v.csrfToken;
      capabilities.value = await api("/api/capabilities");
    } finally {
      ready.value = true;
    }
  }
  async function logout() {
    const r = await api<{ redirect: string }>("/auth/logout", {
      method: "POST",
    });
    user.value = null;
    window.location.href = r.redirect;
  }
  return {
    user,
    organizations,
    organization,
    csrf,
    ready,
    capabilities,
    refresh,
    logout,
  };
});
export async function api<T = any>(
  path: string,
  options: { method?: string; body?: unknown; revision?: string } = {},
): Promise<T> {
  const session = useSession(),
    headers: Record<string, string> = {};
  if (session.organization) headers["x-organization-id"] = session.organization;
  if (session.csrf) headers["x-csrf-token"] = session.csrf;
  if (options.revision) headers["if-match"] = '"' + options.revision + '"';
  const form = options.body instanceof FormData;
  if (options.body && !form) headers["content-type"] = "application/json";
  const res = await fetch(path, {
    method: options.method ?? "GET",
    headers,
    body:
      options.body === undefined
        ? undefined
        : form
          ? (options.body as FormData)
          : JSON.stringify(options.body),
  });
  let body;
  try {
    body = await res.json();
  } catch {
    body = { message: "Serveren returnerede ikke et gyldigt svar." };
  }
  if (!res.ok) {
    if (res.status === 401) session.user = null;
    throw new ApiError(
      res.status,
      body.code ?? "ERROR",
      body.message ?? "Handlingen mislykkedes.",
      body.details,
    );
  }
  return body as T;
}
export function errorText(error: unknown) {
  return error instanceof Error
    ? error.message
    : "Handlingen kunne ikke gennemføres.";
}
export const dateTime = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("da-DK", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value))
    : "Ikke kørt endnu";
